/**
 * One measure on the standard Ballot Initiatives report (PRD v2 §5.17.3).
 *
 * The citizen Ballot Card's order in Pro tokens: eyebrow (cycle · designation),
 * type and status chips, title, summary with Show more, who supports / opposes,
 * what a YES / NO vote means, background, fiscal impact, document links, a
 * Blue Book disclosure when the entry is readable, the organization's filled-in
 * custom fields, and — on My — the workspace's tracking footer. The citizen
 * `MeasureCard` itself cannot be reused: its palette is scoped to the citizen
 * tree (CLAUDE.md), so the content blocks come from the Pro detail cards.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Flame } from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import api from '../../lib/api';
import Chip from '../Chip';
import RichContent from '../RichContent';
import { OpenExternalIcon } from '../icons';
import { WorkspaceBadgeList } from '../WorkspaceBadge';
import { measureTypeLabel } from '../../constants/ballotInitiatives';
import { richTextIsEmpty } from '../../utils/richText';
import {
  initiativeDisplayName,
  initiativeNumberLine,
  measureDocumentLinks,
  positionColorClasses,
  positionLabel,
} from './initiativeDisplay';
import {
  blueBookAttribution, blueBookIsReadable, blueBookSectionLabel, measureBlueBookUrl, readableBlueBookSections,
} from './blueBook';
import { SummaryCard, SidesCard, VoteMeaningCard, FiscalCard } from './detail/MeasureContentCards';
import { ProvenancedText } from './detail/detailAtoms';
import { getCustomFieldDisplayValues, type CandidateCustomFieldColumn } from '../candidates/candidateCustomFieldUtils';
import type { InitiativeReportRow, BallotInitiativeDetail } from './types';

export interface InitiativeReportCardProps {
  initiative: InitiativeReportRow;
  /** My tab: show the workspace position / hot / priority / notes footer. */
  showTracking?: boolean;
  /** Organization custom fields that hold a value somewhere in this report. */
  customFieldColumns?: CandidateCustomFieldColumn[];
  /** Public share: no authenticated fetches (the Blue Book disclosure needs one). */
  isPublicView?: boolean;
}

const DISCLOSURE_SUMMARY_CLASS =
  'cursor-pointer text-sm font-medium text-primary-600 hover:underline dark:text-primary-400';

/**
 * The Blue Book sections, fetched only when the reader opens the disclosure —
 * the list row carries the entry status (enough to offer the disclosure) but
 * not the sections themselves, exactly as the citizen card does (§5.16).
 */
const BlueBookDisclosure: React.FC<{ initiative: InitiativeReportRow }> = ({ initiative }) => {
  const [open, setOpen] = useState(false);
  const detail = useQuery({
    queryKey: ['ballot-initiative', initiative.id],
    enabled: open,
    queryFn: async () => {
      const response = await api.get(`/ballot-initiatives/${initiative.id}`);
      return (response.data.data ?? response.data) as BallotInitiativeDetail;
    },
    staleTime: 5 * 60 * 1000,
  });
  const sections = detail.data ? readableBlueBookSections(detail.data) : [];
  const bookUrl = (detail.data && measureBlueBookUrl(detail.data)) || measureBlueBookUrl(initiative);

  return (
    <details className="mt-3" onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary className={DISCLOSURE_SUMMARY_CLASS}>The Blue Book</summary>
      <div className="mt-2 rounded-lg bg-background-secondary p-3 dark:bg-background-dark-tertiary">
        <p className="mb-3 text-xs italic text-text-tertiary dark:text-text-dark-tertiary">
          {blueBookAttribution(detail.data?.blue_book_edition ?? initiative.blue_book_edition)}
          {bookUrl && (
            <>
              {' · '}
              <a
                href={bookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 not-italic font-medium text-primary-600 hover:underline dark:text-primary-400"
              >
                Read the booklet
                <OpenExternalIcon className="h-3 w-3" />
              </a>
            </>
          )}
        </p>
        {detail.isLoading && (
          <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading the Blue Book…</p>
        )}
        {detail.isError && (
          <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">The Blue Book could not be loaded.</p>
        )}
        {detail.data && sections.length === 0 && (
          <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No Blue Book sections are available yet.</p>
        )}
        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.section}>
              <div className="text-xs font-semibold uppercase text-text-tertiary dark:text-text-dark-tertiary mb-1">
                {blueBookSectionLabel(section.section)}
                {section.page_reference ? <span className="font-normal normal-case"> · p. {section.page_reference}</span> : null}
              </div>
              <RichContent html={section.body} className="text-sm text-text-secondary dark:text-text-dark-secondary" />
            </div>
          ))}
        </div>
      </div>
    </details>
  );
};

const InitiativeReportCard: React.FC<InitiativeReportCardProps> = ({
  initiative,
  showTracking = false,
  customFieldColumns = [],
  isPublicView = false,
}) => {
  const [backgroundOpen, setBackgroundOpen] = useState(false);
  // On a phone the cards collapse to their identity and open on tap — thirteen
  // measures of full detail is a lot of scrolling (Angie, 2026-09-13). From
  // md up every card is open and the summary is inert, as before.
  const isDesktop = useMediaQuery('(min-width: 768px)');
  // The report already names its cycle and shows on-ballot measures only
  // (Angie, 2026-09-13), so the eyebrow is the designation line alone.
  const eyebrow = initiativeNumberLine(initiative);
  const documents = measureDocumentLinks(initiative);
  const hasBackground = !richTextIsEmpty(initiative.background_context);
  const provenance = initiative.content_provenance ?? {};
  const workspaces = initiative.workspaces ?? [];
  const hasNotes = Boolean(initiative.tracking_notes && initiative.tracking_notes.trim());
  const hasWorkspaceTracking = showTracking && (
    Boolean(initiative.position) || Boolean(initiative.is_hot) || (initiative.priority ?? 0) > 0 || hasNotes || workspaces.length > 0
  );

  const customFieldRows = customFieldColumns
    .map((col) => ({ col, values: getCustomFieldDisplayValues(initiative, col.definitionId) }))
    .filter(({ values }) => values.length > 0);

  return (
    <details
      className="card p-4 sm:p-5 group"
      open={isDesktop || undefined}
      data-testid="initiative-report-card"
    >
      <summary
        className="list-none [&::-webkit-details-marker]:hidden cursor-pointer md:cursor-default md:pointer-events-none flex items-start justify-between gap-3"
        aria-label={`${initiativeDisplayName(initiative)} — show details`}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold tabular-nums text-primary-700 dark:text-primary-300">
              {eyebrow || 'Ballot measure'}
            </span>
            <Chip label={measureTypeLabel(initiative.measure_type)} variant="neutral" size="xs" />
          </div>
          <h3 className="mt-1.5 text-lg font-semibold leading-snug text-text-primary dark:text-text-dark-primary">
            {initiativeDisplayName(initiative)}
          </h3>
        </div>
        <span className="md:hidden mt-1 shrink-0 text-text-tertiary dark:text-text-dark-tertiary" aria-hidden>
          <ChevronDown className="h-5 w-5 group-open:hidden" />
          <ChevronUp className="h-5 w-5 hidden group-open:block" />
        </span>
      </summary>

      <div className="mt-4 flex flex-col gap-4 print:flex">
      <SummaryCard initiative={initiative} bare />
      <SidesCard initiative={initiative} bare />
      <VoteMeaningCard initiative={initiative} bare />

      {hasBackground && (
        <details onToggle={(e) => setBackgroundOpen((e.currentTarget as HTMLDetailsElement).open)}>
          <summary className={DISCLOSURE_SUMMARY_CLASS}>
            <span className="inline-flex items-center gap-1">
              Background
              {backgroundOpen ? <ChevronUp className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
            </span>
          </summary>
          <div className="mt-2">
            <ProvenancedText text={initiative.background_context as string} provenance={provenance.background_context} />
          </div>
        </details>
      )}

      <FiscalCard initiative={initiative} bare />

      {(documents.length > 0 || (!isPublicView && blueBookIsReadable(initiative.blue_book_entry_status))) && (
        <div>
          {documents.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {documents.map((document) => (
                <a
                  key={document.label}
                  href={document.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border border-border-light dark:border-border-dark text-text-primary dark:text-text-dark-primary bg-background-tertiary dark:bg-background-dark-tertiary hover:border-text-tertiary dark:hover:border-text-dark-tertiary transition-colors"
                >
                  {document.label}
                  <OpenExternalIcon className="w-3 h-3 text-text-tertiary dark:text-text-dark-tertiary" />
                </a>
              ))}
            </div>
          )}
          {!isPublicView && blueBookIsReadable(initiative.blue_book_entry_status) && (
            <BlueBookDisclosure initiative={initiative} />
          )}
        </div>
      )}

      {customFieldRows.length > 0 && (
        <dl className="space-y-1 border-t border-border-light dark:border-border-dark pt-3" data-testid="initiative-custom-fields">
          {customFieldRows.map(({ col, values }) => (
            <div key={col.definitionId} className="flex gap-2 text-sm">
              <dt className="font-medium text-text-primary dark:text-text-dark-primary shrink-0">{col.name}:</dt>
              <dd className="text-text-secondary dark:text-text-dark-secondary">
                {values.map((value, index) => (
                  <span key={`${value.text}-${index}`} className="block">
                    {value.text}
                    {value.workspaceName && (
                      <span className="text-text-tertiary dark:text-text-dark-tertiary"> ({value.workspaceName})</span>
                    )}
                  </span>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {hasWorkspaceTracking && (
        <footer className="border-t border-border-light dark:border-border-dark pt-3 space-y-2" data-testid="initiative-tracking-footer">
          {workspaces.length > 0 ? (
            <WorkspaceBadgeList
              workspaces={workspaces.map((ws) => ({
                id: ws.id,
                name: ws.name,
                position: ws.position ? positionLabel(ws.position) : undefined,
              }))}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {initiative.position && (
                <span className={`inline-flex items-center px-2 py-1 rounded-md font-medium ${positionColorClasses(initiative.position)}`}>
                  {positionLabel(initiative.position)}
                </span>
              )}
              {initiative.is_hot && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-medium bg-error-100 text-error-700 dark:bg-error-900/20 dark:text-error-300">
                  <Flame className="h-3.5 w-3.5" aria-hidden /> Hot
                </span>
              )}
              {(initiative.priority ?? 0) > 0 && (
                <span className="text-text-secondary dark:text-text-dark-secondary">Priority {initiative.priority}</span>
              )}
            </div>
          )}
          {hasNotes && (
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary whitespace-pre-line">
              {initiative.tracking_notes}
            </p>
          )}
        </footer>
      )}
      </div>
    </details>
  );
};

export default InitiativeReportCard;
